import React, { ChangeEvent, useCallback, useMemo } from 'react'
import { Autocomplete, CustomChangeEventType } from '@manychat/manyui'

import { isFilterFieldValuePrimitive } from 'common/filter/guards'
import { useFilterFieldValues } from 'common/filter/hooks'

import { Input } from './Input'

type Value = string | number

interface MainOption {
  value: Value
  label: string
}

interface TextAutocompleteProps {
  fieldId: string
  value: string
  placeholder: string
  onChange: (event: { target: { value: Value } }, closePopover: boolean) => void
}

export const TextAutocomplete = (props: TextAutocompleteProps) => {
  const fieldValues = useFilterFieldValues({
    fieldId: props.fieldId,
    query: props.value,
  })
  const { value, ...rest } = props
  const options: MainOption[] = useMemo(
    () =>
      fieldValues.items.map((item) => {
        const value = isFilterFieldValuePrimitive(item) ? item : item.value

        return {
          value,
          label: String(value),
        }
      }),
    [fieldValues.items],
  )

  const { onChange } = props
  const handleChange = useCallback(
    (event: CustomChangeEventType<MainOption, MainOption>) => {
      const modifiedEvent = {
        ...event,
        target: {
          ...event.target,
          value: event.target.option.value || '',
        },
      }

      onChange(modifiedEvent, true)
    },
    [onChange],
  )

  const handleInputRef = useCallback((element?: { select: () => void } | null) => {
    if (element) {
      element.select()
    }
  }, [])

  const handleQueryChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event, false)

      fieldValues.fetch({
        fieldId: props.fieldId,
        query: event.target.value,
      })
    },
    [onChange, fieldValues, props.fieldId],
  )

  return (
    <Autocomplete
      {...rest}
      label=""
      ref={handleInputRef}
      options={options}
      value={value}
      onChange={handleChange}
      onQueryChange={handleQueryChange}
      customInput={<Input />}
      emptyLabel="No suggestions found"
      allowNew={false}
      noPortal
      noClearOnEmpty
    />
  )
}
